import { AuthInfoWithCnf, ENVIRONMENT, knownAADClientInfo } from '@citrite/msal-browser';
import {
	hasFeatureCanary,
	PlatformDependencies,
	versionInfo,
} from '@citrite/workspace-ui-platform';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { getFromLocalStorage } from 'javascript/sf/Storage';
import { AADSSOICAContents } from 'Workspace/BrowserExtension/aadSSOSpecificIcaFileParser';
import { container } from 'Workspace/DependencyManagement';
import { lastUserDetailsKey } from 'Workspace/UserContext/constants';
import { UserDetailsCacheEntry } from 'Workspace/UserContext/UserContextLoader';

//This feild is sent to Chrome app from the Native client
// So that they can use if for the MSAL login hint
// Selecting a name which indicates WSP usage instead MSAL specific
export const userAuthAliasKeyForICAData = 'UserAuthAlias';

export function wsuiAuthEnvironment() {
	const wsuiInfo = versionInfo.get();
	return wsuiInfo.environment === 'release' && wsuiInfo.release !== 'master'
		? ENVIRONMENT.PROD
		: ENVIRONMENT.NON_PROD;
}

// Until we get the proper values from ICA file, we will use the default values
// This is specifically required for Hybrid Testing until broker code is ready
export function getDefaultAADClientInfo() {
	return wsuiAuthEnvironment() === ENVIRONMENT.PROD
		? knownAADClientInfo.ProductionCommercial
		: knownAADClientInfo.StagingCommercial;
}

export function isFeatureEnabledCitrixAAD_SSO_GA(): boolean {
	const workspaceConfiguration = container
		.resolve(PlatformDependencies.WorkspaceConfiguration)
		.get();
	return hasFeatureCanary(workspaceConfiguration, FeatureFlag.EnableGACitrixAADSSO);
}

/*
	This is required since native clients will pass back verbatim , the new AADSSO from the ICA file , 
	We need to convert these two field names to the correct field names in the AuthInfo object. 
	AzureSSOnResourceAppId   ---> WSUI has to convert to resourceAppId
	AzureSSOnAuthorizeUrl    ---> WSUI has to convert to authority

	The following other two fields from the ICA file, the CWA Native apps will convert and send to WSUI
	to preserve backward compatibility: 

	AzureSSOnClientId     ---> The CWA Native apps will convert to -----> clientId  
	AzureSSOnRedirectUri  ---> The CWA Native apps will convert to -----> redirectUri
*/
export function parseJsonFromNativeToAuthInfo(jsonPayload: string): AuthInfoWithCnf {
	const rawParsedAuthInfo: AuthInfoWithCnf &
		Pick<AADSSOICAContents, 'AzureSSOnResourceAppId' | 'AzureSSOnAuthorizeUrl'> =
		JSON.parse(jsonPayload);

	//until the GA is enabled the default client info should be from MicrosoftSample
	const defaultClientInfo = isFeatureEnabledCitrixAAD_SSO_GA()
		? getDefaultAADClientInfo()
		: knownAADClientInfo.MicrosoftSample;
	const authInfo: AuthInfoWithCnf = {
		...rawParsedAuthInfo,
		authority: rawParsedAuthInfo.AzureSSOnAuthorizeUrl ?? defaultClientInfo.authority,
		resourceAppId:
			rawParsedAuthInfo.AzureSSOnResourceAppId ?? defaultClientInfo.resourceAppId,
	};

	return authInfo;
}

export function getUpnOrAuthAliasForCurrentUser(): string | undefined {
	try {
		const lastLoggedUserFromStorage =
			getFromLocalStorage<UserDetailsCacheEntry>(lastUserDetailsKey);
		return (
			lastLoggedUserFromStorage?.upn || lastLoggedUserFromStorage?.authAlias || undefined
		); // we don't want falsy strings like empty strings either
	} catch (error) {
		/** Ignore errors. Empty loginHint is fine */
	}

	return undefined;
}
